import {createContext, PropsWithChildren, useCallback, useContext, useEffect, useMemo, useState} from "react";
import * as React from "react";
import {
    ThemeOptions,
    ThemeProvider,
    useActivateListener,
    useDeactivateListener,
    useStorageItemValue
} from "@variocube/cube-ui";
import {LauncherItem} from "@variocube/app-api";
import {Carriers, CubeCarrier} from "./types";
import {logger} from "./logger";

interface CarrierContext {
    carrierId?: string;
    setCarrierId: (carrier: string) => void;
    carriers?: Carriers;
    authenticatedCarrier?: string;
    setAuthenticatedCarrier: (carrier?: string) => void;
    cubeCarrier?: CubeCarrier;
}

const CARRIER_CONTEXT = createContext<CarrierContext | undefined>(undefined);

const log = logger.sub("CarrierProvider");

export function CarrierProvider({children}: PropsWithChildren<{}>) {
    const carriers = useStorageCarriers();
    const [carrierId, setCarrierId] = useState<string>();
    const [authenticatedCarrier, setAuthenticatedCarrier] = useState<string>();

    // auto-select a single carrier
    useEffect(() => {
        if (carriers) {
            const ids = Object.keys(carriers);
            if (ids.length == 1) {
                log.info("Setting single carrier", ids[0]);
                setCarrierId(ids[0]);
            }
        }
    }, [carriers]);

    const cubeCarrier = useMemo<CubeCarrier | undefined>(() => {
        if(carriers && carrierId) {
            return carriers[carrierId];
        }
    }, [carriers, carrierId]);

    const handleActivate = useCallback((item?: LauncherItem) => {
        if (item) {
            log.info("Setting carrier from launcher item", {item});
            setCarrierId(item.id);
            setAuthenticatedCarrier(undefined);
        }
    }, []);
    useActivateListener(handleActivate);

    const handleDeactivate = useCallback(async() => {
        setAuthenticatedCarrier(undefined);
    }, []);
    useDeactivateListener(handleDeactivate);

    const theme = useMemo(() => themes[carrierId ?? "dpd"], [carrierId]);

    const contextValue = useMemo(() => ({
        carrierId,
        setCarrierId,
        carriers,
        authenticatedCarrier,
        setAuthenticatedCarrier,
        cubeCarrier,
    }), [carrierId, setCarrierId, carriers, authenticatedCarrier, setAuthenticatedCarrier]);

    return (
        <ThemeProvider defaultTheme={theme}>
            <CARRIER_CONTEXT.Provider value={contextValue}>
                {children}
            </CARRIER_CONTEXT.Provider>
        </ThemeProvider>
    );
}

export function useCarrierContext() {
    const context = useContext(CARRIER_CONTEXT);
    if (!context) {
        throw new Error("No carrier context found. Are you missing CarrierProvider in your component tree?");
    }
    return context;
}

export function useCarrierId() {
    return useCarrierContext().carrierId;
}

export function useCubeCarrier() {
    return useCarrierContext().cubeCarrier;
}

export function useSetCarrierId() {
    return useCarrierContext().setCarrierId;
}

export function useCarriers() {
    return useCarrierContext().carriers;
}

export function useAuthenticatedCarrier() {
    return useCarrierContext().authenticatedCarrier;
}

export function useSetAuthenticatedCarrier() {
    return useCarrierContext().setAuthenticatedCarrier;
}

function useStorageCarriers() {
    // read `carriers` with fallback to `carrierAccessCode`
    const carriers = useStorageItemValue<Carriers>("carriers");
    const carrierAccessCode = useStorageItemValue<Carriers>("carrierAccessCode");
    return carriers ?? carrierAccessCode;
}

const themes = {
    "dpd": {
        colorBackground: "#fff",
        colorForeground: "hsl(346,10%,20%)",
        colorPrimary: "hsl(346,100%,43%)",
        colorSeparator: "hsl(346,20%,30%)",
        colorInputBackground: "#fff",
    },
    "gls": {
        colorBackground: "#fff",
        colorForeground: "#212126",
        colorPrimary: "#061ab1",
        colorSeparator: "#373740",
        colorInputBackground: "#fff",
    },
    "ups": {
        colorBackground: "#fff",
        colorForeground: "#301506",
        colorPrimary: "#fab80a",
        colorSeparator: "#301506",
        colorInputBackground: "#fff",
    },
    "dhl": {
        colorBackground: "#fff",
        colorForeground: "#212126",
        colorPrimary: "#d40612",
        colorSeparator: "#373740",
        colorInputBackground: "#fff",
    },
    "physical": {
        colorBackground: "#fff",
        colorForeground: "#2882c0",
        colorPrimary: "rgb(177, 200, 0)",
        colorSeparator: "rgb(177, 200, 0)",
        colorInputBackground: "#fff",
    },
    "wienbox": {
        colorBackground: "#fff",
        colorForeground: "#09255B",
        colorPrimary: "#09255B",
        colorSeparator: "#95C8E5",
        colorInputBackground: "#fff",
    },
    "greenpickup": {
        colorBackground: "#fff",
        colorForeground: "#114232",
        colorPrimary: "#114232",
        colorSeparator: "#40BF94",
        colorInputBackground: "#fff",
    },
    "kepoev": {
        colorBackground: "#fff",
        colorForeground: "#09255B",
        colorPrimary: "#09255B",
        colorSeparator: "#95C8E5",
        colorInputBackground: "#fff",
    }
} as Record<string, ThemeOptions>;